import React from 'react';
import { BrowserRouter as Router, Outlet, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import styles from './App.module.css';
import { AuthProvider } from './Modules/Auth/contexts/AuthContext';
import AuthWrapper from './Modules/Auth/components/AuthWrapper/AuthWrapper';
import ProtectedRoute from './Modules/Auth/components/ProtectedRoute/ProtectedRoute';
import AuthPageLayout from './Modules/Auth/components/AuthPageLayout/AuthPageLayout';
import SignIn from './Modules/Auth/features/SignIn/SignIn';
import ForgotPassword from './Modules/Auth/features/ForgotPassword/ForgotPassword';
import TiktokCollectionsPage from './Pages/Collections/TiktokCollectionsPage/TiktokCollectionsPage';
import ConfirmPassword from './Modules/Auth/features/ConfirmPassword/ConfirmPassword';
import RedirectToDefault from './Modules/Navigation/components/RedirectToDefault/RedirectToDefault';
import { OptionsProvider } from './contexts/Options';
import ReactTooltip from 'react-tooltip';
import Songs from './Pages/Explore/Songs/Songs';
import TiktokSongSuggestionsPage from './Pages/Detect/TiktokSongSuggestionsPage/TiktokSongSuggestionsPage';
import TrendingTiktokAudioSuggestionsPage from './Pages/Detect/TrendingTiktokAudioSuggestionsPage/TrendingTiktokAudioSuggestionsPage';
import TrendingAudiosPage from './Pages/Explore/TrendingAudios/TrendingAudiosPage';
import ArtistAdminPage from './Pages/Admin/ArtistAdminPage';
import SongAdminPage from './Pages/Admin/SongAdminPage';
import SongCollectionsPage from './Pages/Collections/SongCollectionsPage/SongCollectionsPage';
import { ClientPageWrapper, UserClientsContextProvider } from './contexts/UserClientsContext';
import ClientSongsPage from './Pages/Client/ClientSongsPage';
import ClientAudiosPage from './Pages/Client/ClientAudiosPage';
import ClientSongCollectionsPage from './Pages/Client/ClientSongCollectionsPage';
import TiktokAudiosPage from './Pages/Explore/TiktokAudio/TiktokAudiosPage';
import DataDownloadPage from './Pages/Admin/DataDownloadPage';
import InstagramAudiosPage from './Pages/Explore/InstagramAudios/InstagramAudiosPage';
import FeatureFlagsMenu from './utility/featureFlags/FeatureFlagsMenu/FeatureFlagsMenu';

function App() {
    return (
        <div className={styles.App}>
            <AuthProvider>
                <UserClientsContextProvider>
                    <Router>
                        <Routes>
                            <Route path="/" element={<RedirectToDefault />} />

                            <Route
                                element={
                                    <AuthPageLayout>
                                        <Outlet />
                                    </AuthPageLayout>
                                }
                            >
                                <Route path="/sign-in/" element={<SignIn />} />
                                <Route path="/forgot-password/" element={<ForgotPassword />} />
                                <Route path="/confirm-password/" element={<ConfirmPassword />} />
                            </Route>

                            <Route
                                element={
                                    <AuthWrapper>
                                        <OptionsProvider>
                                            <Outlet />
                                        </OptionsProvider>
                                    </AuthWrapper>
                                }
                            >
                                <Route
                                    path="/explorer/tiktok/audio/"
                                    element={
                                        <ProtectedRoute groups={['explorer_tiktok_viewer']} hasAccessWhenMatches="all">
                                            <TiktokAudiosPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/explorer/instagram/audio/"
                                    element={
                                        <ProtectedRoute
                                            groups={['explorer_instagram_viewer']}
                                            hasAccessWhenMatches="all"
                                        >
                                            <InstagramAudiosPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/explorer/tiktok/trending-audios/"
                                    element={
                                        <ProtectedRoute groups={['user_admin']} hasAccessWhenMatches="all">
                                            <TrendingAudiosPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/explorer/songs/"
                                    element={
                                        <ProtectedRoute groups={[]} hasAccessWhenMatches="all">
                                            <Songs />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/collections/tiktok/"
                                    element={
                                        <ProtectedRoute groups={['catalogue_viewer']} hasAccessWhenMatches="all">
                                            <TiktokCollectionsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/collections/songs"
                                    element={
                                        <ProtectedRoute groups={[]} hasAccessWhenMatches="all">
                                            <SongCollectionsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/detect/songs/"
                                    element={
                                        <ProtectedRoute groups={['user_admin']} hasAccessWhenMatches="all">
                                            <TiktokSongSuggestionsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/detect/tiktok/trending-audios/"
                                    element={
                                        <ProtectedRoute groups={['user_admin']} hasAccessWhenMatches="all">
                                            <TrendingTiktokAudioSuggestionsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/admin/artist/"
                                    element={
                                        <ProtectedRoute groups={['user_admin']} hasAccessWhenMatches="all">
                                            <ArtistAdminPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/admin/song/"
                                    element={
                                        <ProtectedRoute groups={['user_admin']} hasAccessWhenMatches="all">
                                            <SongAdminPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/admin/data-download/"
                                    element={
                                        <ProtectedRoute
                                            groups={['user_admin', 'trnds_admin']}
                                            hasAccessWhenMatches="any"
                                        >
                                            <DataDownloadPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="client"
                                    element={
                                        <ProtectedRoute
                                            groups={['trnds_client', 'trnds_admin']}
                                            hasAccessWhenMatches="any"
                                        >
                                            <ClientPageWrapper>
                                                <Outlet />
                                            </ClientPageWrapper>
                                        </ProtectedRoute>
                                    }
                                >
                                    <Route path="explorer/songs/" element={<ClientSongsPage />} />
                                    <Route path="explorer/tiktok/audio/" element={<ClientAudiosPage />} />
                                    <Route path="collections/songs/" element={<ClientSongCollectionsPage />} />
                                </Route>
                            </Route>
                        </Routes>
                    </Router>
                </UserClientsContextProvider>
            </AuthProvider>
            <ToastContainer />
            <ReactTooltip id="global" />
            <FeatureFlagsMenu />
        </div>
    );
}

export default App;
